// web/src/console/sections/UsersSection.tsx
import type { JSX } from "react";
import { useState } from "react";
import { consoleApi } from "../../lib/consoleApi";
import { adminApi } from "../../lib/adminApi";
import { getUser } from "../../lib/auth";
import { useConsole } from "../ConsoleContext";
import { useAsync } from "../useAsync";
import { usePaged } from "../usePaged";
import { Pager } from "../Pager";
import { StatCard } from "../charts";
import { CardTitle } from "../Term";
import { fmtRelative, fmtTokens } from "../format";
import { UserLink } from "../UserLink";
import { OnlineIndicator } from "../OnlineIndicator";
import { AdminCreateUser } from "../../components/chat/AdminCreateUser";
import { AdminUserManagement } from "../../components/chat/AdminUserManagement";
import { IconPlus } from "../../components/icons";


export function UsersSection(): JSX.Element {
  const me = getUser();
  const { tick } = useConsole();
  // Token usage Top N — compact panel below the user table (7-day window, live-tick).
  const usageAsync = useAsync(() => adminApi.usage({ range: "7d" }), [tick]);
  // The paged users fetch drives both the table and the stat cards (the stats
  // summarise the visible page — same single request, no duplicate fetch).
  const paged = usePaged((off) => consoleApi.fetchUsers({ offset: off }).then((r) => r.users), [tick]);
  const annos = useAsync(() => consoleApi.fetchAnnotations(), [tick]);
  // CURRENT online presence (#228 Phase 2): the set of user ids online right now
  // drives the per-row indicator and the "当前在线" count.
  const presence = useAsync(() => consoleApi.fetchPresence(), [tick]);
  const [panel, setPanel] = useState<"none" | "create" | "manage">("none");

  if (paged.loading) return <div className="cc-skeleton" style={{ height: 200 }} />;
  if (paged.error) return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={paged.reload}>重试</button></div>;

  const list = paged.rows;
  const down = (annos.data?.annotations ?? []).filter((a) => a.kind === "rating");
  const onlineIds = new Set(presence.data?.onlineUserIds ?? []);
  // CURRENT (live, shrinks on delete) vs HISTORICAL (event-log, never shrinks).
  const currentConvs = list.reduce((s, u) => s + u.currentConversations, 0);
  const historicalConvs = list.reduce((s, u) => s + u.historicalConversations, 0);
  return (
    <>
      <div className="cc-topbar" style={{ border: "none", padding: 0, marginBottom: 12 }}>
        <button className="cc-range" onClick={() => setPanel("create")}><IconPlus size={13} /> 新建用户</button>
        <button className="cc-range" onClick={() => setPanel("manage")}>用户管理</button>
      </div>
      <div className="cc-grid-stats">
        <StatCard label="用户" value={String(list.length)} />
        <StatCard label="当前在线" value={String(presence.data?.onlineUsers ?? 0)} {...(presence.data && presence.data.onlineUsers > 0 ? { tone: "ok" as const } : {})} />
        <StatCard label="当前会话" value={String(currentConvs)} />
        <StatCard label="历史会话" value={String(historicalConvs)} />
        <StatCard label="差评反馈" value={String(list.reduce((s, u) => s + u.thumbsDown, 0))} {...(down.length ? { tone: "warn" as const } : {})} />
      </div>
      <div className="cc-card" style={{ marginTop: 12 }}>
        <CardTitle>用户活动</CardTitle>
        <div className="cc-row-meta" style={{ marginBottom: 8 }}>
          当前会话为非删除快照（删除会话后减少）；历史会话来自不可变事件日志，永不回退。
        </div>
        <table className="cc-table">
          <thead><tr><th>用户</th><th>状态</th><th>当前会话</th><th>历史会话</th><th>消息</th><th>差评</th><th>最近活跃</th></tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.userId}>
                <td><UserLink userId={u.userId} username={u.username} /> <span style={{ color: "var(--text-tertiary)" }}>· {u.tenant}</span></td>
                <td><OnlineIndicator online={onlineIds.has(u.userId)} /></td>
                <td>{u.currentConversations}</td><td>{u.historicalConversations}</td><td>{u.messages}</td><td>{u.thumbsDown}</td>
                <td>{u.lastActiveAt ? fmtRelative(u.lastActiveAt) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={paged.page} hasMore={paged.hasMore} onPrev={paged.prev} onNext={paged.next} loading={paged.loading} />
      </div>
      {panel === "create" && me && <AdminCreateUser defaultTenant={me.tenant} onClose={() => { setPanel("none"); paged.reload(); }} />}
      {panel === "manage" && me && <AdminUserManagement selfId={me.id} onClose={() => { setPanel("none"); paged.reload(); }} />}
      {/* Token 用量 (Top) — compact panel from adminApi.usage topUsers (7-day window). */}
      {usageAsync.data !== null && usageAsync.data.topUsers.length > 0 && (
        <div className="cc-card" style={{ marginTop: "var(--space-3)" }}>
          <CardTitle>用户 token 用量（近 7 天 Top）</CardTitle>
          <table className="cc-table">
            <thead><tr><th>用户</th><th style={{ textAlign: "right" }}>token 用量</th></tr></thead>
            <tbody>
              {usageAsync.data.topUsers.slice(0, 10).map((u) => (
                <tr key={u.actor}>
                  <td>{u.actor}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtTokens(u.totalTokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
